// Driving distance/duration between two points via the Google Routes API -
// same approach fusion_admindash uses (GoogleMapDirections.tsx), not the
// classic DistanceMatrix/Directions service and not a haversine estimate.

export interface LatLng {
  lat: number;
  lng: number;
}

export interface DrivingDistanceLeg {
  distanceMeters: number;
  durationSeconds: number;
}

export interface DrivingDistanceResult {
  distanceMeters: number;
  durationSeconds: number;
  // One entry per hop when `waypoints` is passed to getDrivingDistance:
  // origin->waypoint1, waypoint1->waypoint2, ..., lastWaypoint->destination.
  // Used for Multi-City's per-leg night-charge calculation.
  legs?: DrivingDistanceLeg[];
}

export async function getDrivingDistance(
  origin: LatLng,
  destination: LatLng,
  waypoints?: LatLng[]
): Promise<DrivingDistanceResult | null> {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    console.error("REACT_APP_GOOGLE_MAPS_API_KEY is not configured");
    return null;
  }

  try {
    const response = await fetch(
      "https://routes.googleapis.com/directions/v2:computeRoutes",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask":
            "routes.duration,routes.distanceMeters,routes.legs.duration,routes.legs.distanceMeters",
        },
        body: JSON.stringify({
          origin: {
            location: {
              latLng: { latitude: origin.lat, longitude: origin.lng },
            },
          },
          destination: {
            location: {
              latLng: {
                latitude: destination.lat,
                longitude: destination.lng,
              },
            },
          },
          ...(waypoints && waypoints.length > 0
            ? {
                intermediates: waypoints.map((point) => ({
                  location: {
                    latLng: { latitude: point.lat, longitude: point.lng },
                  },
                })),
              }
            : {}),
          travelMode: "DRIVE",
          routingPreference: "TRAFFIC_AWARE",
        }),
      }
    );

    if (!response.ok) {
      console.error("Routes API request failed:", response.status, await response.text());
      return null;
    }

    const data = await response.json();
    const route = data.routes?.[0];
    if (!route) return null;

    const parseDuration = (duration: unknown) =>
      parseInt(String(duration).replace("s", ""), 10) || 0;

    const legs: DrivingDistanceLeg[] | undefined = route.legs?.map(
      (leg: { distanceMeters?: number; duration?: unknown }) => ({
        distanceMeters: leg.distanceMeters ?? 0,
        durationSeconds: parseDuration(leg.duration),
      })
    );

    return {
      distanceMeters: route.distanceMeters ?? 0,
      durationSeconds: parseDuration(route.duration),
      legs,
    };
  } catch (error) {
    console.error("Failed to fetch driving distance:", error);
    return null;
  }
}

export function formatDistance(meters: number): string {
  return `${(meters / 1000).toFixed(1)} km`;
}

export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes} min`;
}
